import { WebAuth } from 'auth0-js';
import { jwtDecode as decode } from 'jwt-decode';

import { baseUrl, clientID, domain, homeUrl } from '@constants/config.ts';
import { participantDefault } from '@constants/proptypes';

import { errorAlert, confirmDialog } from './Alerts';
import AuthError from './AuthError.ts';
import { getItem, removeItem, setItem } from './ls.ts';
import {
  getItem as sessionGet,
  removeItem as sessionRemove,
  setItem as sessionSet
} from './ss.ts';
import { isPathValid, sanitizeQueryParameters } from './url.ts';

const audience = ''.concat(baseUrl).concat('/api');
const redirectUri = ''.concat(homeUrl).concat('/callback');
const responseType = 'token id_token';
const scope = `
  openid
  profile
  email
  create:retros
  update:retros
  delete:retros
  create:orgs
  update:orgs
  delete:orgs
  create:teams
  update:teams
  delete:teams
  create:notes
  update:notes
  delete:notes
  create:metrics
  update:metrics
  delete:metrics
  create:actions
  update:actions
  delete:actions
  read:users
  read:retros
  read:orgs
  read:teams
  read:notes
  read:actions
  read:metrics
  read:integrations
  create:integrations
  update:integrations
  delete:integrations`
  .trim()
  .replace(/\s+/g, ' ');

const LOGIN_DATA_STORAGE_KEY = 'evetro:login-data';
const ACCESS_TOKEN_STORAGE_KEY = 'evetro:accessToken';
const USER_PROFILE_STORAGE_KEY = 'evetro:userProfile';
const NONCE_KEY = 'evetro:login-nonce';
const PROFILE_CACHE_FALLBACK_MS = 5 * 60 * 1000;

const isObject = obj => obj !== null && typeof obj === 'object';

export function setLoginMetaData(obj) {
  sessionSet(LOGIN_DATA_STORAGE_KEY, JSON.stringify(obj));
}

/**
 * @param {Object} profile - An object representing the profile
 * of the authenticated user
 * @property {String} profile.sub - The authenticated user's ID
 *   (is defined regardless of the IdP)
 * @property {String} profile.email - The authenticated user's email address
 *   (is defined regardless of the IdP)
 * @returns {Object} A normalized user object containing a first name,
 * a last name, the ID and the email address
 */
function resolveParticipant(profile) {
  const namespace = 'https://attribs.evetro.com/';
  return {
    id: profile.sub,
    firstName: profile[`${namespace}firstName`],
    lastName: profile[`${namespace}lastName`],
    mail: profile.email,
    isGuestUser: profile.sub?.startsWith?.('evetro-guest|') ?? true
  };
}

let accessTokenVar = null;
let tokenRenewalTimeout = null;
let accessTokenExpiry = 0;
let hasParsedLocationHash = false;

const persistAccessToken = (accessToken, expiresAt) => {
  accessTokenExpiry = expiresAt;
  sessionSet(
    ACCESS_TOKEN_STORAGE_KEY,
    JSON.stringify({ accessToken, expiresAt })
  );
};

const clearPersistedAccessToken = () => {
  accessTokenVar = null;
  accessTokenExpiry = 0;
  clearTimeout(tokenRenewalTimeout);
  sessionRemove(ACCESS_TOKEN_STORAGE_KEY);
};

const cacheUserProfile = (profile, expiresAt) => {
  let cacheExpiry = Date.now() + PROFILE_CACHE_FALLBACK_MS;
  if (!!expiresAt && expiresAt > Date.now()) {
    cacheExpiry = expiresAt;
  }
  sessionSet(
    USER_PROFILE_STORAGE_KEY,
    JSON.stringify({ profile, expiresAt: cacheExpiry })
  );
};

const clearCachedUserProfile = () => {
  sessionRemove(USER_PROFILE_STORAGE_KEY);
};

const getCachedUserProfile = () => {
  const cached = sessionGet(USER_PROFILE_STORAGE_KEY);
  if (!cached) {
    return null;
  }
  const { profile, expiresAt } = JSON.parse(cached);
  if (expiresAt && expiresAt <= Date.now()) {
    clearCachedUserProfile();
    return null;
  }
  return profile || null;
};

const restoreAccessTokenFromStorage = () => {
  const stored = sessionGet(ACCESS_TOKEN_STORAGE_KEY);
  if (!stored) {
    return;
  }
  const { accessToken, expiresAt } = JSON.parse(stored);
  if (expiresAt && expiresAt <= Date.now()) {
    clearPersistedAccessToken();
    return;
  }
  accessTokenVar = accessToken;
  if (expiresAt) {
    accessTokenExpiry = expiresAt;
    scheduleTokenRenewal(expiresAt);
  } else {
    accessTokenExpiry = 0;
  }
};

const webAuth = new WebAuth({
  audience,
  clientID,
  domain,
  redirectUri,
  responseType,
  scope
});

export function logout() {
  clearCachedUserProfile();
  clearPersistedAccessToken();
  webAuth.logout({ clientID, returnTo: `${homeUrl}/kthxbai` });
}

const checkSession = () =>
  new Promise((resolve, reject) => {
    webAuth.checkSession({}, (err, authResult) => {
      if (err != null) {
        reject(err);
      } else if (authResult && authResult.accessToken) {
        resolve(authResult);
      } else reject(new AuthError('No valid access token received.'));
    });
  });

const scheduleTokenRenewal = expiresAt => {
  if (!expiresAt) {
    return;
  }
  const delay = expiresAt - Date.now();
  if (delay <= 0) {
    return;
  }
  clearTimeout(tokenRenewalTimeout);
  tokenRenewalTimeout = setTimeout(() => {
    checkSession()
      .then(setAccessToken)
      .catch(() => {
        clearPersistedAccessToken();
      });
  }, delay);
};

const getUserInfo = token =>
  new Promise((resolve, reject) => {
    webAuth.client.userInfo(token, (err, profile) => {
      if (err != null) {
        reject(err);
      } else if (profile == null || typeof profile !== 'object') {
        reject(new AuthError('Received profile object was invalid.'));
      } else resolve(profile);
    });
  });

const removeGuestLogin = () => removeItem('evetro-guest-password');

const getGuestToken = () => getItem('evetro-guest-password');

const parseHash = hash =>
  new Promise((resolve, reject) => {
    webAuth.parseHash({ hash }, (err, authResult) => {
      if (err != null) {
        reject(err);
      } else if (authResult && authResult.accessToken) {
        resolve(authResult);
      } else {
        reject(new AuthError('No valid access token received.'));
      }
    });
  });

function setAccessToken({ accessToken, expiresIn }) {
  setItem('isLoggedOnEvetro', 'true');
  const timeout = Number(expiresIn) * 1000;
  let expiresAt = 0;
  if (Number.isFinite(timeout) && timeout > 0) {
    expiresAt = Date.now() + timeout;
  }
  accessTokenVar = accessToken;
  persistAccessToken(accessToken, expiresAt);
  if (expiresAt) {
    scheduleTokenRenewal(expiresAt);
  } else {
    clearTimeout(tokenRenewalTimeout);
  }
  return accessToken;
}

export function setGuestToken(string) {
  setItem('evetro-guest-password', string);
}

restoreAccessTokenFromStorage();

export function login() {
  removeGuestLogin();
  const location = sessionGet(LOGIN_DATA_STORAGE_KEY);
  if (location) {
    const { pathname, search } = JSON.parse(location);
    sessionRemove(LOGIN_DATA_STORAGE_KEY);
    if (!isPathValid(pathname)) return;
    const nonce = randomHexString(16);
    sessionSet(NONCE_KEY, nonce);
    const sanitizedSearch = sanitizeQueryParameters(search);
    const redirectTo = `${pathname}${sanitizedSearch}`;
    const state = encodeURIComponent(JSON.stringify({ nonce, redirectTo }));
    webAuth.authorize({ state });
    return;
  }
  webAuth.authorize();
}

export function getTokenType() {
  if (getItem('isLoggedOnEvetro') && accessTokenVar) {
    return 'Bearer';
  }
  if (getGuestToken()) {
    return 'Guest';
  }
  return null;
}

export function getAuthToken() {
  if (getItem('isLoggedOnEvetro') && accessTokenVar) {
    return accessTokenVar;
  }
  return getGuestToken();
}

export function getAuthorization() {
  const authorization = `${getTokenType()} ${getAuthToken()}`;
  return { authorization };
}

export function clearCredentials() {
  removeItem('isLoggedOnEvetro');
  clearPersistedAccessToken();
  clearCachedUserProfile();
}

export async function setSession(locationHash, onUserLoggedIn, navigate) {
  if (hasParsedLocationHash) {
    return;
  }
  hasParsedLocationHash = true; // TODO check if the useEffect fucks us over
  let forceLogout = false;
  let location = '';
  let profile = {
    sub: null,
    email: '',
    given_name: 'Some',
    family_name: 'User'
  };
  try {
    const authResult = await parseHash(locationHash);
    const stateObj = parseJson(authResult?.state);
    if (isObject(stateObj)) {
      const { nonce, redirectTo } = stateObj;
      const expectedNonce = sessionGet(NONCE_KEY);
      if (nonce && nonce === expectedNonce) {
        sessionRemove(NONCE_KEY);
        if (isPathValid(redirectTo)) {
          const [path, ...query] = redirectTo.split('?');
          const search = sanitizeQueryParameters('?'.concat(query.join('')));
          location = path.concat(search);
        }
      }
    }
    profile = await getUserInfo(setAccessToken(authResult));
  } catch (authError) {
    const { error, errorDescription } = authError;
    if (errorDescription === 'Please verify your email before logging in.') {
      await confirmDialog(
        {
          title: 'Verification Email Sent',
          text: 'Check your inbox and confirm your email address.',
          showCancelButton: false,
          focusConfirm: true,
          icon: 'success'
        },
        false
      );
      forceLogout = true;
    } else {
      await errorAlert({
        title: error || 'OH NOES!',
        text: errorDescription || 'No information available :('
      });
    }
  } finally {
    if (forceLogout) {
      logout();
    } else {
      const participant = resolveParticipant(profile);
      cacheUserProfile(participant, accessTokenExpiry);
      onUserLoggedIn(participant);
      if (location) {
        navigate?.(location);
      }
    }
  }
}

export async function renewSession() {
  return setAccessToken(await checkSession());
}

export async function loadMyUser() {
  const cachedUser = getCachedUserProfile();
  if (cachedUser) {
    return Promise.resolve(cachedUser);
  }
  if (getItem('isLoggedOnEvetro') && !getItem('evetro-guest-hash')) {
    return renewSession()
      .then(getUserInfo)
      .then(resolveParticipant)
      .then(participant => {
        cacheUserProfile(participant, accessTokenExpiry);
        return participant;
      })
      .catch(() => Promise.resolve(participantDefault));
  }
  if (
    window.location.pathname !== '/' &&
    window.location.pathname !== '/callback'
  ) {
    try {
      const payload = decode(getGuestToken());
      if (payload.exp <= Math.round(Date.now() / 1000)) {
        // The token has expired
        removeGuestLogin();
        clearCachedUserProfile();
        return Promise.resolve(participantDefault);
      }
      const guestUser = {
        id: payload.sub,
        firstName: payload.userName,
        lastName: '',
        mail: '',
        isGuestUser: true
      };
      cacheUserProfile(guestUser, payload.exp * 1000);
      return Promise.resolve(guestUser);
    } catch {
      /* offer log service here */
    }
  }
  return Promise.resolve(participantDefault);
}

/**
 * @param {number} - amount of characters in string
 * @returns {string} - a hexadecimal encoded string
 * */
function randomHexString(numChars) {
  const numBytes = Math.ceil(numChars / 2);
  const randomBytes = new Uint8Array(numBytes);
  window.crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes)
    .map(n => n.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, numChars);
}

function parseJson(input) {
  try {
    return JSON.parse(decodeURIComponent(input));
  } catch {
    return null;
  }
}
